import React, { useEffect, useMemo, useState } from 'react';

const PRODUCT_QUERY = `
  query getProduct($id: ID!) {
    product(id: $id) {
      title
      descriptionHtml
      variants(first: 10) {
        edges {
          node {
            id
            title
            price {
              amount
              currencyCode
            }
            availableForSale
          }
        }
      }
      images(first: 5) {
        edges {
          node {
            url
            altText
          }
        }
      }
    }
  }
`;

export function ShopifyProduct({ shopifyClient, onAddToCart, productId }) {
    const [product, setProduct] = useState(null);
    const [selectedVariantId, setSelectedVariantId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [adding, setAdding] = useState(false);
    const [sizesOpen, setSizesOpen] = useState(false);

    const sizeTable = useMemo(() => {
        const html = product?.descriptionHtml;
        if (!html) return null;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const table = doc.querySelector('table');
        if (!table) return null;

        // Build a 2D grid from all rows
        const grid = Array.from(table.querySelectorAll('tr')).map((row) =>
            Array.from(row.querySelectorAll('th, td')).map((cell) => cell.textContent.trim())
        );
        if (grid.length < 2 || grid[0].length < 2) return null;

        // Original layout: grid[0] = ['', 'S', 'M', 'L', ...]
        //                  grid[n] = ['Width, cm', '51', '56', ...]
        // Transposed: rows = sizes, cols = measurements
        const measureRows = grid.slice(1);

        // Shorten long measurement labels for column headers
        const shortenLabel = (label) => {
            const withoutUnit = label.split(',')[0].trim();
            const words = withoutUnit.split(' ');
            return words.slice(0, 2).join(' ');
        };

        const headers = ['Size', ...measureRows.map((r) => shortenLabel(r[0]))];
        const dataRows = grid[0].slice(1).map((sizeName, j) =>
            [sizeName, ...measureRows.map((r) => r[j + 1])]
        );

        return { headers, dataRows };
    }, [product?.descriptionHtml]);

    useEffect(() => {
        shopifyClient
            .request(PRODUCT_QUERY, {
                variables: { id: `gid://shopify/Product/${productId}` },
            })
            .then(({ data, errors }) => {
                if (errors || !data.product) {
                    setError('Could not load product.');
                    return;
                }
                setProduct(data.product);
                const firstAvailable = data.product.variants.edges.find(
                    (e) => e.node.availableForSale
                );
                setSelectedVariantId(
                    firstAvailable
                        ? firstAvailable.node.id
                        : data.product.variants.edges[0]?.node.id
                );
            })
            .catch(() => setError('Could not load product.'))
            .finally(() => setLoading(false));
    }, [shopifyClient, productId]);

    const handleAddToCart = async () => {
        if (!selectedVariantId) return;
        setAdding(true);
        await onAddToCart(selectedVariantId);
        setAdding(false);
    };

    if (loading) {
        return <p className='product-loading'>Loading product…</p>;
    }

    if (error || !product) {
        return <p className='product-error'>{error || 'Product not found.'}</p>;
    }

    const variants = product.variants.edges.map((e) => e.node);
    const selectedVariant =
        variants.find((v) => v.id === selectedVariantId) || variants[0];
    const images = product.images.edges.map((e) => e.node);
    const firstImage = images[0];

    const formatPrice = ({ amount, currencyCode }) =>
        new Intl.NumberFormat('en', {
            style: 'currency',
            currency: currencyCode,
        }).format(parseFloat(amount));

    return (
        <div className='product-card'>
            {firstImage ? (
                <img
                    className='product-card__image'
                    src={firstImage.url}
                    alt={firstImage.altText || product.title}
                />
            ) : (
                <div className='product-card__image-placeholder' />
            )}
            <div className='product-card__body'>
                <h3 className='product-card__title'>{product.title}</h3>
                {selectedVariant && (
                    <p className='product-card__price'>
                        {formatPrice(selectedVariant.price)}
                    </p>
                )}
                {variants.length > 1 && (
                    <select
                        className='product-card__variant-select'
                        aria-label={`Select ${product.title} variant`}
                        value={selectedVariantId}
                        onChange={(e) => setSelectedVariantId(e.target.value)}
                    >
                        {variants.map((v) => (
                            <option key={v.id} value={v.id} disabled={!v.availableForSale}>
                                {v.title}{!v.availableForSale ? ' — Sold out' : ''}
                            </option>
                        ))}
                    </select>
                )}
                {sizeTable && (
                    <div className='product-card__sizes'>
                        <button
                            className='product-card__sizes-toggle'
                            onClick={() => setSizesOpen((o) => !o)}
                            aria-expanded={sizesOpen}
                        >
                            <span>Size guide</span>
                            <span className={`product-card__sizes-chevron${sizesOpen ? ' product-card__sizes-chevron--open' : ''}`}>&#8964;</span>
                        </button>
                        <div className={`product-card__sizes-content${sizesOpen ? ' product-card__sizes-content--open' : ''}`}>
                            <div className='product-card__sizes-inner'>
                                <table className='size-table'>
                                    <thead>
                                        <tr>
                                            {sizeTable.headers.map((h, i) => (
                                                <th key={i}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sizeTable.dataRows.map((row, i) => (
                                            <tr key={i}>
                                                {row.map((cell, j) => (
                                                    <td key={j}>{cell}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <p className='size-table__note'>All measurements in cm</p>
                            </div>
                        </div>
                    </div>
                )}
                <button
                    className='product-card__btn'
                    onClick={handleAddToCart}
                    disabled={adding || !selectedVariant?.availableForSale}
                >
                    {adding ? 'Adding…' : 'Add to cart'}
                </button>
            </div>
        </div>
    );
}
